import React, { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { Link } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import { NavLink } from '../types';
import './CookiePreferencesPage.css';

const CookiePreferencesPage: React.FC = () => {
  const [cookies, setCookie] = useCookies(['cookieConsent']);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    preferences: false
  });

  const navLinks: NavLink[] = [
    { text: 'Home', href: '/' },
    { text: 'Catalog', href: '/catalog' },
    { text: 'Contact', href: '/contact' }
  ];

  useEffect(() => {
    // Load existing preferences
    if (cookies.cookieConsent) {
      setPreferences(cookies.cookieConsent);
    }
  }, [cookies.cookieConsent]);

  const handleEmailSubmit = (email: string): void => {
    console.log('Email subscription:', email);
  };

  const handleFollowShop = (): void => {
    console.log('Follow on shop clicked');
  };

  const handlePreferenceChange = (category: keyof typeof preferences) => {
    if (category === 'necessary') return; // Cannot change necessary cookies
    
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleSavePreferences = () => {
    setCookie('cookieConsent', preferences, { 
      path: '/',
      maxAge: 365 * 24 * 60 * 60, // 1 year
      sameSite: 'lax'
    });
    
    // Show success message or redirect
    alert('Cookie preferences saved successfully!');
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    setPreferences(allAccepted);
    setCookie('cookieConsent', allAccepted, { 
      path: '/',
      maxAge: 365 * 24 * 60 * 60,
      sameSite: 'lax'
    });
    
    alert('All cookies accepted!');
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    
    setPreferences(onlyNecessary);
    setCookie('cookieConsent', onlyNecessary, { 
      path: '/',
      maxAge: 365 * 24 * 60 * 60,
      sameSite: 'lax'
    });
    
    alert('Only necessary cookies accepted!');
  };

  return (
    <div className="App">
      <Header navLinks={navLinks} />
      
      <main className="cookie-preferences-main">
        <div className="cookie-preferences-container">
          <div className="cookie-preferences-header">
            <h1>Cookie Preferences</h1>
            <p className="cookie-preferences-intro">
              Manage your cookie preferences to control how we use cookies on our website.
            </p>
          </div>

          <div className="cookie-preferences-content">
            <div className="cookie-categories">
              {/* Necessary Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <h3>Necessary Cookies</h3>
                  <div className="cookie-toggle disabled">
                    <input 
                      type="checkbox" 
                      checked={preferences.necessary} 
                      disabled 
                      readOnly
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </div>
                <p className="cookie-description">
                  These cookies are essential for the website to function properly. They cannot be disabled.
                </p>
                <ul className="cookie-examples">
                  <li>Session management</li>
                  <li>Security features</li>
                  <li>Basic website functionality</li>
                </ul>
              </div>

              {/* Analytics Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <h3>Analytics Cookies</h3>
                  <div className="cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={preferences.analytics}
                      onChange={() => handlePreferenceChange('analytics')}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </div>
                <p className="cookie-description">
                  These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
                </p>
                <ul className="cookie-examples">
                  <li>Google Analytics</li>
                  <li>Page views and user behavior</li>
                  <li>Website performance metrics</li>
                </ul>
              </div>

              {/* Marketing Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <h3>Marketing Cookies</h3>
                  <div className="cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={preferences.marketing}
                      onChange={() => handlePreferenceChange('marketing')}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </div>
                <p className="cookie-description">
                  These cookies are used to track visitors across websites to display relevant and engaging advertisements.
                </p>
                <ul className="cookie-examples">
                  <li>Social media tracking</li>
                  <li>Advertising networks</li>
                  <li>Retargeting campaigns</li>
                </ul>
              </div>

              {/* Preferences Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <h3>Preference Cookies</h3>
                  <div className="cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={preferences.preferences}
                      onChange={() => handlePreferenceChange('preferences')}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                </div>
                <p className="cookie-description">
                  These cookies remember your choices and preferences to provide a more personalized experience.
                </p>
                <ul className="cookie-examples">
                  <li>Language preferences</li>
                  <li>Theme settings</li>
                  <li>User interface preferences</li>
                </ul>
              </div>
            </div>

            <div className="cookie-actions">
              <div className="cookie-action-buttons">
                <Button 
                  variant="secondary" 
                  size="medium" 
                  onClick={handleRejectAll}
                >
                  Reject All
                </Button>
                
                <Button 
                  variant="primary" 
                  size="medium" 
                  onClick={handleSavePreferences}
                >
                  Save Preferences
                </Button>
                
                <Button 
                  variant="primary" 
                  size="medium" 
                  onClick={handleAcceptAll}
                >
                  Accept All
                </Button>
              </div>
              
              <div className="cookie-info">
                <p>
                  <strong>Note:</strong> You can change your cookie preferences at any time by visiting this page. 
                  Some features of our website may not work properly if you disable certain cookies.
                </p>
                <p>
                  For more information about how we use cookies, please read our{' '}
                  <Link to="/privacy" className="privacy-link">Privacy Policy</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer 
        onEmailSubmit={handleEmailSubmit}
        onFollowShop={handleFollowShop}
      />
    </div>
  );
};

export default CookiePreferencesPage;
